import React from "react"
import { 
  Clock, BadgeCheck, ChevronRight, Package, Layers, 
  Building2, FileCheck2, Calendar, Tag, Hash, FileText, MapPin 
} from "lucide-react"
import { cn } from "@/lib/utils"

// Helper to format date
function formatDate(isoStr: string | null | undefined) {
  if (!isoStr) return "-"
  const d = new Date(isoStr)
  if (isNaN(d.getTime())) return "-"
  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  })
}

export type TargetChipData = {
  key: string
  label: string
  sublabel?: string
  kind: "company" | "product" | "variant" | "external" | "pending"
  verified?: boolean
}

export function resolveTargetSummary(targets: any[], rfq?: any): TargetChipData[] {
  const items = targets || []
  if (items.length === 0 && rfq?.company) {
    return [
      {
        key: rfq.company.id || "company-target",
        label: rfq.company.name || "Unnamed Company",
        sublabel: [rfq.company.city, rfq.company.country].filter(Boolean).join(", "),
        kind: "company",
        verified: rfq.company.verificationStatus === "APPROVED",
      },
    ]
  }
  if (items.length === 0) return []
 
  return items.map((t, i) => {
    if (t.productVariant) {
      const pv = t.productVariant
      const company = pv.product?.company
      return {
        key: t.id || `variant-${i}`,
        label: pv.name || pv.product?.productName || "Unnamed Variant",
        sublabel: company?.name,
        kind: "variant",
        verified: company?.verificationStatus === "APPROVED",
      }
    }
    if (t.product) {
      const company = t.product.company
      return {
        key: t.id || `product-${i}`,
        label: t.product.productName || "Unnamed Product",
        sublabel: company?.name,
        kind: "product",
        verified: company?.verificationStatus === "APPROVED",
      }
    }
    if (t.company) {
      return {
        key: t.id || `company-${i}`,
        label: t.company.name || "Unnamed Company",
        sublabel: [t.company.city, t.company.country].filter(Boolean).join(", "),
        kind: "company",
        verified: t.company.verificationStatus === "APPROVED",
      }
    }
    if (t.externalSeller) {
      return {
        key: t.id || `external-${i}`,
        label: t.externalSeller.companyName,
        sublabel: [t.externalSeller.locality, t.externalSeller.country].filter(Boolean).join(", "),
        kind: "external",
      }
    }
    return {
      key: t.id || `pending-${i}`,
      label: t.productId
        ? `Product ID: ${String(t.productId).slice(0, 8).toUpperCase()}`
        : "Pending match",
      kind: "pending",
    }
  })
}

export function TargetChip({ data }: { data: TargetChipData }) {
  if (data.kind === "pending") {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full border border-dashed border-slate-300 px-3 py-1 text-[11px] font-semibold text-slate-400 italic">
        <Clock className="size-3" />
        {data.label}
      </span>
    )
  }

  const isExternal = data.kind === "external"
  const initial = data.label?.charAt(0)?.toUpperCase() || "?"

  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full py-1 pl-1 pr-3 text-[11px] font-semibold",
        isExternal
          ? "border border-dashed border-slate-300 bg-slate-50 text-slate-600"
          : "border border-primary/20 bg-white text-slate-700"
      )}
    >
      <span
        className={cn(
          "flex size-5 shrink-0 items-center justify-center rounded-full text-[9px] font-bold",
          isExternal ? "bg-slate-200 text-slate-500" : "bg-primary text-primary-foreground"
        )}
      >
        {initial}
      </span>
      <span className="max-w-[140px] truncate">{data.label}</span>
      {!isExternal && data.verified && (
        <BadgeCheck className="size-3.5 text-emerald-600 shrink-0" />
      )}
      {isExternal && (
        <span className="border-l border-slate-300 pl-1.5 text-[9px] font-bold uppercase tracking-wide text-slate-400">
          External
        </span>
      )}
    </span>
  )
}

const STATUS_STYLES: Record<string, string> = {
  OPEN: "bg-emerald-50 text-emerald-700 border-emerald-100",
  FORWARDED: "bg-indigo-50 text-indigo-700 border-indigo-100",
  QUOTED: "bg-blue-50 text-blue-700 border-blue-100",
  PENDING: "bg-amber-50 text-amber-700 border-amber-100",
  REQUEST_REVISION: "bg-amber-50 text-amber-700 border-amber-100",
  EXPIRED: "bg-red-50 text-red-700 border-red-100",
  CANCELLED: "bg-red-50 text-red-700 border-red-100",
  REJECTED: "bg-red-50 text-red-700 border-red-100",
  CLOSED: "bg-slate-50 text-slate-700 border-slate-200",
  CLOSE: "bg-slate-50 text-slate-700 border-slate-200",
}

export interface RfqCardProps {
  rfq: any
  role: "buyer" | "seller" | "admin"
  onSelect: (rfq: any) => void
  adminSubTab?: "buyers" | "sellers"
}

export function RfqCard({ rfq, role, onSelect, adminSubTab }: RfqCardProps) {
  const rfqStatus = (rfq.status || "").toUpperCase()
  const isExpired = rfqStatus === "OPEN" && rfq.validUntil ? new Date(rfq.validUntil) < new Date() : false
  const resolvedStatus = isExpired ? "EXPIRED" : (rfqStatus === "CLOSE" ? "CLOSED" : rfqStatus || "OPEN")
  const statusClass = STATUS_STYLES[resolvedStatus] || "bg-slate-50 text-slate-700 border-slate-200"

  const chips = role !== "seller" ? resolveTargetSummary(rfq.targets || [], rfq) : []
  const visibleChips = chips.slice(0, 3)
  const overflowCount = chips.length - visibleChips.length

  const specs = [
    rfq.moq ? { label: "MOQ", value: rfq.moq } : null,
    rfq.quantity ? { label: "Quantity", value: rfq.quantity + " " + rfq.measureUnit } : null,
    rfq.budget ? { label: "Budget", value: rfq.currencyCode + " " + rfq.budget } : null,
    rfq.paymentTerms ? { label: "Payment", value: rfq.paymentTerms } : null,
    rfq.estimatedTime ? { label: "Timeline", value: rfq.estimatedTime } : null,
  ].filter(Boolean) as { label: string; value: string }[]

  const daysToExpiry = rfq.validUntil
    ? Math.ceil((new Date(rfq.validUntil).getTime() - Date.now()) / (1000 * 60 * 60 * 24))
    : null
  const expirySoon = daysToExpiry !== null && daysToExpiry >= 0 && daysToExpiry <= 5

  // Date prefix based on role
  const dateLabel = role === "seller" ? "Received" : role === "admin" ? (adminSubTab === "buyers" ? "Received" : "Sent") : "Sent"

  // Primary CTA label
  let ctaText = "Details"
  if (role === "seller" && resolvedStatus === "OPEN") {
    ctaText = "Add Quotation"
  } else if (role === "admin" && adminSubTab === "buyers" && resolvedStatus === "OPEN") {
    ctaText = "Forward to Seller"
  }

  // Admin & Seller Extra Info
  const buyerName = (role === "admin" && adminSubTab === "buyers") || role === "seller"
    ? (rfq?.buyer?.user?.name || rfq?.buyerRfq?.buyer?.user?.name || rfq?.buyer?.company?.name || rfq?.buyerName)
    : null
  const buyerLogo = (role === "admin" && adminSubTab === "buyers") || role === "seller"
    ? (rfq?.buyer?.company?.logoUrl || rfq?.buyerRfq?.buyer?.company?.logoUrl || rfq?.buyerLogoUrl)
    : null
  const companyName = role === "admin" && adminSubTab === "sellers" ? (rfq?.company?.legalName || rfq?.company?.name) : null
  const companyLogo = role === "admin" && adminSubTab === "sellers" ? rfq?.company?.logoUrl : null

  return (
    <div
      onClick={() => onSelect(rfq)}
      className="group card-luminous cursor-pointer rounded-2xl bg-white p-4 text-left flex flex-col h-full"
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          {buyerName ? (
            <div className="flex items-center gap-2 text-sm font-extrabold text-slate-800">
              <div className="flex size-6 shrink-0 items-center justify-center rounded bg-slate-100 border border-slate-200">
                {buyerLogo ? (
                  <img
                    src={buyerLogo} 
                    alt={buyerName}
                    className="size-4 object-contain"
                  />
                ) : (
                  <Building2 className="size-3.5 text-primary" />
                )}
              </div>
              <span className="text-slate-600 font-bold text-xs shrink-0">
                <span className="hidden sm:inline">Buyer: </span>{buyerName}
              </span>
              <span className="hidden sm:inline text-slate-300 font-normal">|</span>
              <span className="hidden sm:block truncate font-bold text-slate-800 group-hover:text-primary transition-colors">
                {rfq.queryMessage || "Sourcing Inquiry"}
              </span>
            </div>
          ) : companyName ? (
            <div className="flex items-center gap-2 text-sm font-extrabold text-slate-800">
              <div className="flex size-6 shrink-0 items-center justify-center rounded bg-slate-100 border border-slate-200">
                {companyLogo ? (
                  <img
                    src={companyLogo}
                    alt={companyName}
                    className="size-4 object-contain"
                  />
                ) : (
                  <Building2 className="size-3.5 text-primary" />
                )}
              </div>
              <span className="text-slate-600 font-bold text-xs shrink-0">
                <span className="hidden sm:inline">Company: </span>{companyName}
              </span>
              <span className="hidden sm:inline text-slate-300 font-normal">|</span>
              <span className="hidden sm:block truncate font-bold text-slate-800 group-hover:text-primary transition-colors">
                {rfq.queryMessage || "Sourcing Inquiry"}
              </span>
            </div>
          ) : (
            <h3 className="truncate text-sm font-bold text-slate-800 group-hover:text-primary transition-colors">
              {rfq.queryMessage || "Sourcing Inquiry"}
            </h3>
          )}
        </div>
        <span
          className={cn(
            "shrink-0 rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider",
            statusClass
          )}
        >
          {resolvedStatus}
        </span>
      </div>

      {/* Spec row */}
      <div className="mt-3 flex flex-wrap gap-5 min-h-[36px]">
        {specs.map((s) => (
          <div key={s.label}>
            <p className="text-[9px] font-bold uppercase tracking-wider text-slate-400">{s.label}</p>
            <p className="text-xs font-bold text-slate-700 mt-0.5">{s.value}</p>
          </div>
        ))}
        {specs.length === 0 && (
          <div className="invisible select-none pointer-events-none">
            <p className="text-[9px] font-bold uppercase tracking-wider">&nbsp;</p>
            <p className="text-xs font-bold mt-0.5">&nbsp;</p>
          </div>
        )}
      </div>

      {/* Target chips row (hidden on seller panel) */}
      {role !== "seller" && (
        <div className="mt-3 mb-4.5">
          <p className="mb-1.5 text-[9px] font-bold uppercase tracking-wider text-slate-400">
            {chips.length > 0 ? `Target${chips.length === 1 ? "" : "s"} · ${chips.length}` : "Target"}
          </p>
          <div className="flex flex-wrap gap-1.5">
            {chips.length === 0 ? (
              <TargetChip data={{ key: "none", label: "Pending match", kind: "pending" }} />
            ) : (
              <>
                {visibleChips.map((c) => (
                  <TargetChip key={c.key} data={c} />
                ))}
                {overflowCount > 0 && (
                  <span className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[11px] font-semibold text-slate-500">
                    +{overflowCount} more
                  </span>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="mt-auto pt-4 flex items-end justify-between gap-2 border-t border-slate-100">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] font-medium text-slate-400">
          <div className="flex items-center gap-1">
            <span>{dateLabel}</span>
            <span className="font-semibold text-slate-600 bg-slate-100 rounded px-1.5 py-0.5 text-[10px] whitespace-nowrap">
              {formatDate(rfq.createdAt)}
            </span>
          </div>
          {rfq.leadTime && (
            <div className="flex items-center gap-1">
              <span>Lead Time</span>
              <span className="font-semibold text-slate-600 bg-slate-100 rounded px-1.5 py-0.5 text-[10px] whitespace-nowrap">
                {formatDate(rfq.leadTime) !== "-" ? formatDate(rfq.leadTime) : rfq.leadTime}
              </span>
            </div>
          )}
        </div>
        <button
          onClick={(e) => {
            e.stopPropagation()
            onSelect(rfq)
          }}
          className="flex items-center gap-0.5 text-xs font-bold text-primary hover:text-primary-hover hover:underline cursor-pointer shrink-0 pb-0.5"
        >
          {ctaText} <ChevronRight className="size-3.5" />
        </button>
      </div>
    </div>
  )
}

export function RfqCardSkeleton() {
  return (
    <div className="rounded-2xl border-[0.5px] border-slate-200/70 bg-white p-4 shadow-sm animate-fast-pulse space-y-4 text-left">
      <div className="flex justify-between gap-4">
        <div className="flex items-center gap-2 flex-1">
          <div className="size-6 rounded bg-slate-200" />
          <div className="h-4 w-32 rounded bg-slate-200" />
          <div className="h-4 w-2 bg-slate-200" />
          <div className="h-4 flex-1 rounded bg-slate-200" />
        </div>
        <div className="h-5 w-16 rounded bg-slate-200" />
      </div>
      <div className="h-px bg-slate-200 w-full" />
      <div className="flex gap-2">
        <div className="h-6 w-20 rounded-full bg-slate-200" />
        <div className="h-6 w-24 rounded-full bg-slate-200" />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-1">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="h-10 rounded bg-slate-200" />
        ))}
      </div>
      <div className="h-px bg-slate-200 w-full" />
      <div className="flex justify-between pt-1">
        <div className="h-3 w-40 rounded bg-slate-200" />
        <div className="h-4 w-16 rounded bg-slate-200" />
      </div>
    </div>
  )
}
